/**
 * Purpose: The single place that interprets a barcode string. Converts any scanned, OCR'd,
 * typed, imported or fetched code into a canonical 14-digit GTIN, or rejects it outright.
 * Inputs: Raw decoded/typed text, plus the scanner's reported symbology when known.
 * Outputs: A 14-digit GTIN string, or null when the input can't be used as a product barcode.
 */
import type { BarcodeSymbology } from '@lib/models/types'

/** GTIN-13-style restricted-circulation prefixes: in-store/variable-measure, internal, coupons. */
const RESTRICTED_GTIN13_PREFIXES = ['02', '04', '05', '98', '99']

/** GTIN-8 prefixes reserved for restricted-circulation (in-store) use. */
const RESTRICTED_GTIN8_PREFIXES = ['0', '2']

function isRestrictedGtin13Prefix(prefix2: string): boolean {
  const asNumber = Number(prefix2)
  return RESTRICTED_GTIN13_PREFIXES.includes(prefix2) || (asNumber >= 20 && asNumber <= 29)
}

/**
 * GS1 mod-10 check digit, computed over the 13-digit payload of a left-padded 14-digit GTIN.
 * Padding with leading zeros never changes the sum, so one weighting works for every input
 * length once it's padded — the digit next to the check digit always carries weight 3.
 */
function hasValidGs1CheckDigit(gtin14: string): boolean {
  let sum = 0
  for (let i = 0; i < 13; i++) {
    const weight = i % 2 === 0 ? 3 : 1
    sum += Number(gtin14[i]) * weight
  }
  const expected = (10 - (sum % 10)) % 10
  return expected === Number(gtin14[13])
}

/** Zero-pads an 8, 12, 13 or 14 digit string to 14 digits. Any other length returns null. */
function padToGtin14(digits: string): string | null {
  if (![8, 12, 13, 14].includes(digits.length)) return null
  return digits.padStart(14, '0')
}

/**
 * Expands an 8-character UPC-E code (number system digit + 6 data digits + check digit) to its
 * 12-digit UPC-A equivalent, following the standard zero-suppression table (same algorithm ZXing
 * uses in UPCEReader#convertUPCEtoUPCA, so it agrees with what the camera scanner decoded).
 */
function expandUpcEToUpcA(upce: string): string | null {
  if (upce.length !== 8) return null
  const numberSystem = upce[0]
  const data = upce.slice(1, 7)
  const checkDigit = upce[7]
  const lastDigit = data[5]

  let middle: string
  switch (lastDigit) {
    case '0':
    case '1':
    case '2':
      middle = data.slice(0, 2) + lastDigit + '0000' + data.slice(2, 5)
      break
    case '3':
      middle = data.slice(0, 3) + '00000' + data.slice(3, 5)
      break
    case '4':
      middle = data.slice(0, 4) + '00000' + data[4]
      break
    default:
      middle = data.slice(0, 5) + '0000' + lastDigit
      break
  }
  return numberSystem + middle + checkDigit
}

/** Takes the GTIN out of a GS1 Digital Link URL path (`/01/<digits>`). */
function extractFromDigitalLink(input: string): { digits: string } | 'not_a_url' | null {
  let url: URL
  try {
    url = new URL(input)
  } catch {
    return 'not_a_url'
  }
  const match = url.pathname.match(/\/01\/(\d+)(?:\/|$)/)
  if (!match || ![8, 12, 13, 14].includes(match[1].length)) return null
  return { digits: match[1] }
}

/** Takes the GTIN out of a GS1 element string carrying application identifier 01. */
function extractFromElementString(input: string, symbology?: BarcodeSymbology): string | null {
  const isDataCarrierForAI01 = symbology === 'data_matrix' || symbology === 'qr_code'
  let rest: string | null = null
  if (input.startsWith('(01)')) rest = input.slice(4)
  else if (isDataCarrierForAI01 && input.startsWith('01')) rest = input.slice(2)
  if (rest === null) return null
  const gtin = rest.slice(0, 14)
  return /^\d{14}$/.test(gtin) ? gtin : null
}

/** Rejects a padded GTIN whose prefix identifies a restricted-circulation code. */
function isRestrictedCirculation(gtin14: string, wasOriginallyEightDigits: boolean): boolean {
  if (wasOriginallyEightDigits) {
    return RESTRICTED_GTIN8_PREFIXES.includes(gtin14.slice(6, 7))
  }
  return isRestrictedGtin13Prefix(gtin14.slice(1, 3))
}

/** Pads, validates the check digit, and rejects a restricted prefix. Null on any failure. */
function finalize(digits: string, wasOriginallyEightDigits: boolean): string | null {
  const padded = padToGtin14(digits)
  if (!padded) return null
  if (!hasValidGs1CheckDigit(padded)) return null
  if (isRestrictedCirculation(padded, wasOriginallyEightDigits)) return null
  return padded
}

/**
 * Converts any scanned, typed, OCR'd, imported or fetched barcode into a canonical GTIN-14, or
 * returns null when it can't be used as a product barcode. See design.md §1 for the full
 * decision order this follows.
 */
export function normalizeGtin(input: string, symbology?: BarcodeSymbology): string | null {
  if (symbology === 'unsupported') return null

  const digitalLink = extractFromDigitalLink(input)
  if (digitalLink !== 'not_a_url') {
    if (digitalLink === null) return null
    return finalize(digitalLink.digits, digitalLink.digits.length === 8)
  }

  const elementStringGtin = extractFromElementString(input, symbology)
  if (elementStringGtin) return finalize(elementStringGtin, false)

  const cleaned = input.replace(/[\s-]/g, '')
  if (!/^\d+$/.test(cleaned)) return null

  if (symbology === 'upc_e') {
    const upcA = expandUpcEToUpcA(cleaned)
    return upcA ? finalize(upcA, false) : null
  }

  // An 8-digit code with no reported symbology (OCR or typed) is ambiguous between EAN-8 and a
  // compressed UPC-E. Try the literal EAN-8 reading first; only fall back to UPC-E expansion if
  // that doesn't produce a valid GTIN.
  if (cleaned.length === 8 && !symbology) {
    const asEan8 = finalize(cleaned, true)
    if (asEan8) return asEan8
    const upcA = expandUpcEToUpcA(cleaned)
    return upcA ? finalize(upcA, false) : null
  }

  return finalize(cleaned, cleaned.length === 8)
}

/**
 * Converts a GTIN-14 to the form Open Beauty Facts stores and expects: 13 digits, or 8 digits
 * for a code that was originally a GTIN-8. A GTIN-14 with 6+ leading zeros is treated as
 * originally 8 digits — no legitimate 12/13/14-digit retail GTIN has that many leading zeros.
 */
export function toObfCode(gtin: string): string {
  if (gtin.startsWith('000000')) return gtin.slice(6)
  return gtin.slice(1)
}

/** Strips the GTIN-14 padding back to its natural length, for display only. */
export function formatGtinForDisplay(gtin: string): string {
  return toObfCode(gtin)
}
